// ===========================================================================
// Where you are signed in.
//
// The devices block in the account panel, for an account and for a kept name
// alike (server devices.js): this device first, every other one with when it
// was last here and a Sign out beside it, "Sign out everywhere else", and a
// change of password, which signs every other device out as well. And the
// banner that tells the owner, on their other devices, that a new one signed
// in. Since 2026-10-06; until then a sign-in lasted thirty days wherever it
// was, a password reset left every other device in, and nobody could see
// where they were signed in. ASCII only.
// ===========================================================================

import { t } from '../i18n.js';
import { el, toast } from '../dom.js';
import {
  keepsakeDevices,
  signOutKeepsakeDevice,
  signOutKeepsakeOthers,
  changeKeepsakePassword,
  ackKeepsakeNotice,
  keepsakeStatus,
  clearKeepsake,
} from '../keepsake.js';

export { ackKeepsakeNotice, keepsakeStatus, clearKeepsake };

/** "just now", "5 minutes ago", "3 hours ago", "2 days ago", else the date. */
export function whenSeen(iso, nowMs = Date.now()) {
  const at = Date.parse(iso || '');
  if (!Number.isFinite(at)) return '';
  const min = Math.max(0, Math.round((nowMs - at) / 60000));
  if (min < 2) return 'just now';
  if (min < 60) return `${min} minutes ago`;
  const h = Math.round(min / 60);
  if (h < 24) return h === 1 ? 'an hour ago' : `${h} hours ago`;
  const d = Math.round(h / 24);
  if (d < 14) return d === 1 ? 'yesterday' : `${d} days ago`;
  return new Date(at).toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });
}

/** What the block says when a request is refused. */
function refusal(status, data, fallback) {
  if (status === 429) return (data && data.message) || 'Too many tries. Wait a few minutes and try again.';
  if (status === 0) return 'Could not reach Lumi. Check your connection and try again.';
  return fallback;
}

/** The block's calls for an account (account.js). */
export function accountDevicesApi(account) {
  return {
    list: () => account.devices(),
    signOut: (id) => account.signOutDevice(id),
    signOutOthers: () => account.signOutOthers(),
    changePassword: (current, password) => account.changePassword(current, password),
    note: 'You can be signed in on up to 5 devices. Signing in on another signs out the one used least recently.',
  };
}

/** The block's calls for a kept name (keepsake.js). */
export function keepsakeDevicesApi(base) {
  return {
    list: () => keepsakeDevices({ base }),
    signOut: (id) => signOutKeepsakeDevice({ id, base }),
    signOutOthers: () => signOutKeepsakeOthers({ base }),
    changePassword: (current, password) => changeKeepsakePassword({ current, password, base }),
    note: '',
  };
}

/**
 * The devices block.
 * @param {{ list: () => Promise<object[]|null>, signOut: (id: string) => Promise<{status: number, data: object}>, signOutOthers: () => Promise<{status: number, data: object}>, changePassword: (current: string, password: string) => Promise<{status: number, data: object}>, note?: string }} api
 * @param {{ onSignedOutHere?: () => void }} [opts]  told when this device signed itself out
 */
export function renderDevices(api, opts = {}) {
  const list = el('ul', { class: 'devices-list' }, [el('li', { class: 'muted', text: t('Loading...') })]);
  const msg = el('div', { class: 'error devices-msg' });

  function row(d) {
    const when = d.current ? t('This device') : whenSeen(d.seenAt) ? `Last here ${whenSeen(d.seenAt)}` : '';
    const out = el('button', {
      class: 'btn btn-ghost btn-sm',
      text: t('Sign out'),
      onClick: async () => {
        out.disabled = true;
        const r = await api.signOut(d.id);
        if (r.status === 200) {
          if (r.data.self) {
            opts.onSignedOutHere?.();
            return;
          }
          toast(`Signed out: ${d.label || 'a device'}.`);
          load();
          return;
        }
        out.disabled = false;
        msg.textContent = refusal(r.status, r.data, 'Could not sign it out. Try again in a moment.');
      },
    });
    return el('li', { class: `device-row${d.current ? ' current' : ''}` }, [
      el('div', { class: 'device-what' }, [
        el('span', { class: 'device-label', text: d.label || t('A device') }),
        when ? el('span', { class: 'muted device-when', text: when }) : null,
      ].filter(Boolean)),
      d.current ? null : out,
    ].filter(Boolean));
  }

  async function load() {
    const devices = await api.list().catch(() => null);
    if (!devices) {
      list.replaceChildren(el('li', { class: 'muted', text: t('Could not load your devices.') }));
      return;
    }
    list.replaceChildren(...devices.map(row));
    othersBtn.hidden = devices.filter((d) => !d.current).length === 0;
  }

  const othersBtn = el('button', {
    class: 'btn btn-secondary btn-sm',
    hidden: '',
    text: t('Sign out everywhere else'),
    onClick: async () => {
      othersBtn.disabled = true;
      msg.textContent = '';
      const r = await api.signOutOthers();
      othersBtn.disabled = false;
      if (r.status === 200) {
        const n = Number(r.data.signedOut) || 0;
        toast(n ? `Signed out of ${n} other device${n === 1 ? '' : 's'}.` : 'No other device was signed in.');
        load();
        return;
      }
      msg.textContent = refusal(r.status, r.data, 'Could not sign them out. Try again in a moment.');
    },
  });

  // A new password, with the current one: every other device is signed out.
  const current = el('input', { type: 'password', placeholder: t('Current password'), autocomplete: 'current-password', aria: { label: 'Current password' } });
  const next = el('input', { type: 'password', placeholder: t('New password (8+ characters)'), autocomplete: 'new-password', aria: { label: 'New password' } });
  const save = el('button', {
    class: 'btn btn-primary btn-sm',
    text: t('Change password'),
    onClick: async () => {
      msg.textContent = '';
      if (next.value.length < 8) {
        msg.textContent = 'Password must be at least 8 characters.';
        return;
      }
      save.disabled = true;
      const r = await api.changePassword(current.value, next.value);
      save.disabled = false;
      if (r.status === 200) {
        current.value = '';
        next.value = '';
        form.classList.add('hidden');
        passwordBtn.hidden = false;
        const n = Number(r.data.signedOut) || 0;
        toast(n ? `Password changed. ${n} other device${n === 1 ? ' was' : 's were'} signed out.` : 'Password changed.');
        load();
        return;
      }
      msg.textContent = r.status === 401
        ? 'That is not your current password.'
        : r.status === 400
          ? 'Password must be at least 8 characters.'
          : refusal(r.status, r.data, 'Could not change it. Try again in a moment.');
    },
  });
  const form = el('div', { class: 'auth-form auth-fields hidden' }, [current, next, save]);
  const passwordBtn = el('button', {
    class: 'btn btn-ghost btn-sm',
    text: t('Change password'),
    onClick: () => {
      form.classList.remove('hidden');
      passwordBtn.hidden = true;
      current.focus();
    },
  });

  load();
  return el('div', { class: 'keepsake-block devices-block' }, [
    el('strong', { text: t('Where you are signed in') }),
    el('p', { class: 'muted', text: t('Sign out any device you do not recognize, then change your password. Changing it signs out every other device too.') }),
    list,
    el('div', { class: 'buy-row' }, [othersBtn, passwordBtn]),
    form,
    msg,
    api.note ? el('p', { class: 'muted devices-note', text: t(api.note) }) : null,
  ].filter(Boolean));
}

/**
 * Tell the owner a device signed in, on their other devices: what it was and
 * when. "Review devices" opens the account panel; either button dismisses it,
 * here and on every other device (the caller's onDismiss). One at a time.
 * @param {{ at?: string, label?: string|null }} notice
 * @param {{ onReview?: () => void, onDismiss?: () => void }} [opts]
 */
export function showDeviceNotice(notice, opts = {}) {
  if (!notice || document.querySelector('.device-banner')) return;
  const at = Date.parse(notice.at || '');
  const when = Number.isFinite(at)
    ? new Date(at).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
    : '';
  let done = false;
  const close = () => {
    if (done) return;
    done = true;
    banner.remove();
    opts.onDismiss?.();
  };
  const banner = el('div', { class: 'device-banner', role: 'alert' }, [
    el('div', { class: 'device-banner-body' }, [
      el('strong', { text: t('A new device signed in') }),
      el('div', { text: [notice.label || 'A browser', when].filter(Boolean).join(', ') }),
      el('div', { class: 'muted', text: t('If this was not you, sign it out and change your password.') }),
    ]),
    el('div', { class: 'device-banner-actions' }, [
      el('button', {
        class: 'btn btn-secondary btn-sm',
        text: t('Review devices'),
        onClick: () => {
          close();
          opts.onReview?.();
        },
      }),
      el('button', { class: 'btn btn-ghost btn-sm', text: t('It was me'), onClick: close }),
    ]),
  ]);
  document.body.append(banner);
}

export default { renderDevices, accountDevicesApi, keepsakeDevicesApi, showDeviceNotice, whenSeen };
